import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Button,
  ButtonGroup,
  Typography,
  Toolbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Stack,
} from "@mui/material";
import { Add, Delete, Edit, Refresh, Save, Close } from "@mui/icons-material";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("token");

const resources = [
  {
    key: "lugares-trabajo",
    label: "Lugares de trabajo",
    path: "/api/lugares-trabajo",
    idField: "id",
    editableFields: ["nombre", "observacion"],
  },
  {
    key: "situaciones-profesionales",
    label: "Situaciones Profesionales",
    path: "/api/situaciones-profesionales",
    idField: "id",
    editableFields: ["nombre", "observacion"],
  },
  {
    key: "especialidades",
    label: "Especialidades",
    path: "/api/especialidades",
    idField: "id",
    editableFields: ["nombre", "observacion"],
  },
  {
    key: "estados",
    label: "Estados",
    path: "/api/estados",
    idField: "id",
    editableFields: ["nombre", "observacion"],
  },
  {
    key: "paises",
    label: "Países",
    path: "/api/paises",
    idField: "id",
    editableFields: ["codigo_iso", "nombre"],
    defaultNew: { codigo_iso: "", nombre: "" },
  },
  {
    key: "tipos-pago",
    label: "Tipos de Pago",
    path: "/api/tipos-pago",
    idField: "id",
    editableFields: ["nombre", "observacion"],
  },
  {
    key: "titulaciones-principales",
    label: "Titulaciones Principales",
    path: "/api/titulaciones-principales",
    idField: "id",
    editableFields: ["nombre", "observacion"],
  },
];

function useApiClient() {
  return useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const t = token();
      if (t) config.headers["Authorization"] = `Bearer ${t}`;
      return config;
    });
    return instance;
  }, []);
}

function EditDialog({ open, onClose, onSave, initial, fields, title }) {
  const [values, setValues] = useState(initial || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => setValues(initial || {}), [initial]);

  const handleChange = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave(values);
      onClose(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={() => onClose(false)} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fields.map((f) => (
            <TextField
              key={f}
              label={labelFor(f)}
              value={values[f] ?? ""}
              onChange={handleChange(f)}
              fullWidth
              size="small"
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button onClick={handleSubmit} startIcon={saving ? <CircularProgress size={16} /> : <Save />} disabled={saving}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function labelFor(key) {
  switch (key) {
    case "nombre":
      return "Nombre";
    case "observacion":
      return "Observaciones";
    case "codigo_iso":
      return "Código ISO";
    default:
      // prettify
      return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

export default function Catalogos() {
  const api = useApiClient();
  const [activeKey, setActiveKey] = useState(resources[0].key);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editOpen, setEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const resource = useMemo(() => resources.find((r) => r.key === activeKey), [activeKey]);

  const columns = useMemo(() => {
    const seen = new Set();
    const cols = [];
    if (resource?.idField) cols.push(resource.idField);

    const fromEditable = resource?.editableFields || [];
    for (const k of fromEditable) {
      if (!seen.has(k)) {
        seen.add(k);
        cols.push(k);
      }
    }

    if (!rows.length) return cols;

    const sample = rows[0] || {};
    Object.keys(sample).forEach((k) => {
      if (!seen.has(k) && k !== resource.idField && (typeof sample[k] === "string" || typeof sample[k] === "number")) {
        seen.add(k);
        cols.push(k);
      }
    });

    return cols;
  }, [rows, resource]);

  const fetchAll = async () => {
    if (!resource) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(resource.path);
      setRows(Array.isArray(data) ? data : []);
      setPage(0);
    } catch (e) {
      console.error(e);
      setError("No se pudieron cargar los datos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [activeKey]);

  const handleAdd = () => {
    const initial = resource.defaultNew || { nombre: "", observacion: "" };
    setEditInitial(initial);
    setEditOpen(true);
  };

  const handleEdit = (row) => {
    const initial = {};
    for (const k of resource.editableFields) initial[k] = row[k] ?? "";
    initial[resource.idField] = row[resource.idField];
    setEditInitial(initial);
    setEditOpen(true);
  };

  const handleDelete = async (row) => {
    const id = row[resource.idField];
    if (!window.confirm("¿Eliminar el registro seleccionado?")) return;
    try {
      await api.delete(`${resource.path}/${id}`);
      setSnack({ open: true, message: "Eliminado correctamente", severity: "success" });
      setRows((prev) => prev.filter((r) => r[resource.idField] !== id));
    } catch (e) {
      console.error(e);
      setSnack({ open: true, message: "No se pudo eliminar", severity: "error" });
    }
  };

  const saveRecord = async (values) => {
    const id = values[resource.idField];
    const payload = { ...values };
    if (!id) delete payload[resource.idField];

    if (id === undefined || id === null) {
      // CREATE
      const { data } = await api.post(resource.path, payload);
      setRows((prev) => [data, ...prev]);
      setSnack({ open: true, message: "Creado correctamente", severity: "success" });
    } else {
      // UPDATE
      const { data } = await api.put(`${resource.path}/${id}`, payload);
      setRows((prev) => prev.map((r) => (r[resource.idField] === id ? data : r)));
      setSnack({ open: true, message: "Actualizado correctamente", severity: "success" });
    }
  };

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Catálogos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Selecciona un catálogo para listar, crear, editar o eliminar registros.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {resources.map((r) => (
            <Button
              key={r.key}
              variant={activeKey === r.key ? "contained" : "outlined"}
              onClick={() => setActiveKey(r.key)}
            >
              {r.label}
            </Button>
          ))}
        </Box>
      </Paper>

      <Paper>
        <Toolbar sx={{ gap: 1, justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{resources.find((r) => r.key === activeKey)?.label}</Typography>
            <Tooltip title="Recargar">
              <IconButton onClick={fetchAll}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
          <Button startIcon={<Add />} onClick={handleAdd}>
            Agregar
          </Button>
        </Toolbar>

        {loading ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell key={c}>{labelFor(c)}</TableCell>
                    ))}
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((row) => (
                    <TableRow key={row[resource.idField]} hover>
                      {columns.map((c) => (
                        <TableCell key={c}>{String(row[c] ?? "").toString()}</TableCell>
                      ))}
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton onClick={() => handleEdit(row)} size="small">
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton onClick={() => handleDelete(row)} size="small" color="error">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!paginated.length && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1}>
                        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                          No hay registros.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <EditDialog
        open={editOpen}
        onClose={(ok) => {
          setEditOpen(false);
          if (ok) fetchAll();
        }}
        onSave={saveRecord}
        initial={editInitial}
        fields={resource.editableFields}
        title={editInitial?.[resource.idField] ? "Editar registro" : "Nuevo registro"}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
